import { Check, Eye, Loader2, Pencil, Trash2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
	DraggableResponsiveModalContent,
	ResponsiveModal,
	ResponsiveModalContent,
	ResponsiveModalDescription,
	ResponsiveModalHeader,
	ResponsiveModalTitle,
} from '@/components/ui/responsive-dialog'
import { Textarea } from '@/components/ui/textarea'
import { VirtualizedCombobox } from '@/components/ui/virtualized-combobox'
import bookingService from '@/services/booking-service'

export type SalesRecordDialogState = {
	bookingId: number
	userId: number
	userName: string
} | null

type SalesRecordFormRow = {
	id?: number
	booking_id: string
	user_id: number
	total_sales: string
	commission: string
	amount_paid_out: string
	net_commission: string
	invoice_number: string
	comments: string
}

type SalesRecordForm = {
	user_id: number
	record: SalesRecordFormRow
}

const salesRecordColumns: Array<{
	key: keyof SalesRecordFormRow
	label: string
	required?: boolean
	inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
	placeholder: string
	type?: 'text' | 'textarea'
}> = [
	{
		key: 'booking_id',
		label: 'Buchungs-ID',
		required: true,
		inputMode: 'numeric',
		placeholder: '1',
	},
	{
		key: 'total_sales',
		label: 'Gesamtumsatz',
		required: true,
		inputMode: 'decimal',
		placeholder: '0,00',
	},
	{
		key: 'amount_paid_out',
		label: 'Ausgezahlter Betrag',
		required: true,
		inputMode: 'decimal',
		placeholder: '0,00',
	},
	{
		key: 'commission',
		label: 'Provision',
		required: true,
		inputMode: 'decimal',
		placeholder: '0,00',
	},
	{
		key: 'net_commission',
		label: 'Nettoprovision',
		required: true,
		inputMode: 'decimal',
		placeholder: '0,00',
	},
	{
		key: 'invoice_number',
		label: 'Rechnungsnummer',
		placeholder: '1000',
	},
	{
		key: 'comments',
		label: 'Kommentar',
		placeholder: 'Kommentar',
		type: 'textarea',
	},
]

const decimalFieldKeys: Array<keyof SalesRecordFormRow> = [
	'total_sales',
	'commission',
	'amount_paid_out',
	'net_commission',
]

const createEmptySalesRecordRow = (bookingId?: number, userId?: number): SalesRecordFormRow => ({
	booking_id: bookingId?.toString() ?? '',
	user_id: userId ?? 0,
	total_sales: '',
	commission: '',
	amount_paid_out: '',
	net_commission: '',
	invoice_number: '',
	comments: '',
})

const formatDecimalValue = (value?: string | number | null) => {
	if (value === null || value === undefined) return ''
	return formatDecimalForInput(String(value))
}

const mapSalesRecordToFormRow = (record: SalesRecord): SalesRecordFormRow => ({
	id: record.id,
	booking_id: record.booking_id.toString(),
	user_id: record.user_id,
	total_sales: formatDecimalValue(record.total_sales),
	commission: formatDecimalValue(record.commission),
	amount_paid_out: formatDecimalValue(record.amount_paid_out),
	net_commission: formatDecimalValue(record.net_commission),
	invoice_number: record.invoice_number ?? '',
	comments: record.comments ?? '',
})

const normalizeDecimalValue = (value: string) => value.replace(',', '.')

const formatDecimalForInput = (value: string) => {
	const trimmedValue = value.trim()
	if (!trimmedValue) return ''

	const parsedValue = Number(normalizeDecimalValue(trimmedValue))
	if (Number.isNaN(parsedValue)) return value

	return parsedValue.toFixed(2).replace('.', ',')
}

const formatSalesRecordFieldForView = (key: keyof SalesRecordFormRow, value?: string | number) => {
	if (value === null || value === undefined || value === '') return '-'

	if (decimalFieldKeys.includes(key)) {
		return formatDecimalValue(value) || '-'
	}

	return String(value)
}

const toSalesRecordPayload = (record: SalesRecordFormRow, userId: number) => ({
	booking_id: Number(record.booking_id),
	user_id: userId,
	total_sales: Number(normalizeDecimalValue(record.total_sales)),
	commission: Number(normalizeDecimalValue(record.commission)),
	amount_paid_out: Number(normalizeDecimalValue(record.amount_paid_out)),
	net_commission: Number(normalizeDecimalValue(record.net_commission)),
	invoice_number: record.invoice_number.trim() || null,
	comments: record.comments.trim() || null,
})

const getSalesRecordQueryKey = (userId?: number) => ['fetch-sales-records', userId]

const validateSalesRecordRow = (record: SalesRecordFormRow) => {
	if (!record.booking_id.trim()) {
		return 'Bitte geben Sie eine Buchungs-ID an.'
	}

	const hasMissingRequiredValue = [
		record.total_sales,
		record.commission,
		record.amount_paid_out,
		record.net_commission,
	].some(value => !value.trim())
	if (hasMissingRequiredValue) {
		return 'Bitte fuellen Sie alle Pflichtfelder aus.'
	}

	return null
}

type SharedSalesRecordDialogProps = {
	dialogState: SalesRecordDialogState
	onClose: () => void
	onSuccess?: () => void
}

const SalesRecordCreateFields = ({
	control,
	handleDecimalFieldChange,
	handleDecimalFieldBlur,
	bookingOptions,
	isBookingOptionsLoading,
}: {
	control: ReturnType<typeof useForm<SalesRecordForm>>['control']
	handleDecimalFieldChange: (
		value: string,
		onChange: (...event: any[]) => void,
		fieldName: `record.${string}`,
	) => void
	handleDecimalFieldBlur: (
		value: string,
		onChange: (...event: any[]) => void,
		fieldName: `record.${string}`,
	) => void
	bookingOptions: string[]
	isBookingOptionsLoading: boolean
}) => (
	<>
		<div className="hidden lg:block overflow-hidden rounded-[16px] border border-[#ECECEC]">
			<div className="grid grid-cols-[minmax(88px,0.95fr)_minmax(110px,1.1fr)_minmax(96px,1fr)_minmax(96px,1fr)_minmax(110px,1.1fr)_minmax(110px,1.1fr)_minmax(170px,1.55fr)] border-b border-[#ECECEC] bg-[#F7F8F880]">
				{salesRecordColumns.map(column => (
					<div
						key={column.key}
						className="px-3 py-3 text-[11px] leading-4 font-bold uppercase tracking-[0.08em] text-secondary/60 whitespace-normal break-words">
						{column.label}
					</div>
				))}
			</div>
			<div className="grid grid-cols-[minmax(88px,0.95fr)_minmax(110px,1.1fr)_minmax(96px,1fr)_minmax(96px,1fr)_minmax(110px,1.1fr)_minmax(110px,1.1fr)_minmax(170px,1.55fr)]">
				{salesRecordColumns.map(column => (
					<div key={column.key} className="p-2 border-b border-[#ECECEC] last:border-b-0">
						<FormField
							control={control}
							name={`record.${column.key}`}
							rules={column.required ? { required: `${column.label} ist erforderlich` } : undefined}
							render={({ field, fieldState: { error } }) => (
								<FormItem className="h-full">
									<FormControl>
										{column.key === 'booking_id' ? (
											<VirtualizedCombobox
												searchPlaceholder="Buchung waehlen"
												onChange={field.onChange}
												value={String(field.value ?? '')}
												isLoading={isBookingOptionsLoading}
												error={!!error}
												height={isBookingOptionsLoading ? '40px' : '250px'}
												width="100%"
												contentClassName="lg:w-[200px]!"
												options={bookingOptions}
											/>
										) : column.type === 'textarea' ? (
											<Textarea
												{...field}
												className="min-h-11 h-full rounded-[10px] resize-none"
												placeholder={column.placeholder}
											/>
										) : (
											<Input
												{...field}
												className="h-11 rounded-[10px]"
												placeholder={column.placeholder}
												inputMode={column.inputMode}
												onChange={e => {
													if (decimalFieldKeys.includes(column.key)) {
														handleDecimalFieldChange(
															e.target.value,
															field.onChange,
															`record.${column.key}`,
														)
														return
													}

													field.onChange(e.target.value)
												}}
												onBlur={e => {
													field.onBlur()
													if (decimalFieldKeys.includes(column.key)) {
														handleDecimalFieldBlur(
															e.target.value,
															field.onChange,
															`record.${column.key}`,
														)
													}
												}}
											/>
										)}
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				))}
			</div>
		</div>

		<div className="flex flex-col gap-3 lg:hidden">
			<div className="rounded-[16px] border border-[#ECECEC] p-4 flex flex-col gap-3">
				{salesRecordColumns.map(column => (
					<div key={column.key} className="flex flex-col gap-1">
						<p className="text-[11px] font-bold uppercase tracking-[0.08em] text-secondary/60">
							{column.label}
						</p>
						<FormField
							control={control}
							name={`record.${column.key}`}
							rules={column.required ? { required: `${column.label} ist erforderlich` } : undefined}
							render={({ field, fieldState: { error } }) => (
								<FormItem className="h-full">
									<FormControl>
										{column.key === 'booking_id' ? (
											<VirtualizedCombobox
												searchPlaceholder="Buchung waehlen"
												onChange={field.onChange}
												value={String(field.value ?? '')}
												isLoading={isBookingOptionsLoading}
												error={!!error}
												height={isBookingOptionsLoading ? '40px' : '250px'}
												width="100%"
												contentClassName="lg:!w-[200px]"
												options={bookingOptions}
											/>
										) : column.type === 'textarea' ? (
											<Textarea
												{...field}
												className="min-h-10 h-full text-xs rounded-[12px] resize-none"
												placeholder={column.placeholder}
											/>
										) : (
											<Input
												{...field}
												className="rounded-[12px]"
												placeholder={column.placeholder}
												inputMode={column.inputMode}
												onChange={e => {
													if (decimalFieldKeys.includes(column.key)) {
														handleDecimalFieldChange(
															e.target.value,
															field.onChange,
															`record.${column.key}`,
														)
														return
													}

													field.onChange(e.target.value)
												}}
												onBlur={e => {
													field.onBlur()
													if (decimalFieldKeys.includes(column.key)) {
														handleDecimalFieldBlur(
															e.target.value,
															field.onChange,
															`record.${column.key}`,
														)
													}
												}}
											/>
										)}
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				))}
			</div>
		</div>
	</>
)

export const SalesRecordsDialog = ({
	dialogState,
	onClose,
	onSuccess,
}: SharedSalesRecordDialogProps) => {
	const queryClient = useQueryClient()
	const salesRecordForm = useForm<SalesRecordForm>({
		defaultValues: {
			user_id: 0,
			record: createEmptySalesRecordRow(),
		},
		mode: 'all',
	})

	useEffect(() => {
		if (!dialogState) {
			salesRecordForm.reset({
				user_id: 0,
				record: createEmptySalesRecordRow(),
			})
			return
		}

		salesRecordForm.reset({
			user_id: dialogState.userId,
			record: createEmptySalesRecordRow(dialogState.bookingId, dialogState.userId),
		})
	}, [dialogState, salesRecordForm])

	const { data: bookings, status: bookingsStatus } = useQuery(
		['fetch-all-bookings', dialogState?.userId],
		() =>
			bookingService.fetchBookingsData({
				pageNumber: '1',
				pageSize: '999999999999999',
			}),
		{
			enabled: !!dialogState,
			refetchOnWindowFocus: false,
			cacheTime: 1000 * 60 * 2,
			onError: () => {
				toast.error('Error fetching bookings')
			},
		},
	)

	const handleDecimalFieldChange = (
		value: string,
		onChange: (...event: any[]) => void,
		fieldName: `record.${string}`,
	) => {
		const normalizedValue = value.replace('.', ',')

		if (/^\d*(?:[.,]\d{0,2})?$/.test(normalizedValue)) {
			onChange(normalizedValue)
			salesRecordForm.clearErrors(fieldName as keyof SalesRecordForm)
		}
	}

	const handleDecimalFieldBlur = (
		value: string,
		onChange: (...event: any[]) => void,
		fieldName: `record.${string}`,
	) => {
		const formattedValue = formatDecimalForInput(value)
		if (formattedValue) {
			onChange(formattedValue)
			salesRecordForm.clearErrors(fieldName as keyof SalesRecordForm)
		}
	}

	const { mutate: createSalesRecord, isLoading: isSavingSalesRecord } = useMutation({
		mutationKey: 'create-sales-record',
		mutationFn: async (data: SalesRecordForm) => {
			const currentRecords = await bookingService.fetchSalesRecordsByUser(data.user_id)

			return bookingService.replaceSalesRecords({
				user_id: data.user_id,
				records: [
					...currentRecords.map(record =>
						toSalesRecordPayload(mapSalesRecordToFormRow(record), data.user_id),
					),
					toSalesRecordPayload(data.record, data.user_id),
				],
			})
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries(getSalesRecordQueryKey(dialogState?.userId))
			toast.success('Verkaufsdaten erfolgreich gespeichert')
			onClose()
			onSuccess?.()
		},
		onError: (err: any) => {
			toast.error(
				err?.response?.data?.response?.message ??
					'Verkaufsdaten konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.',
			)
		},
	})

	const onSubmit = (data: SalesRecordForm) => {
		const validationError = validateSalesRecordRow(data.record)
		if (validationError) {
			toast.error(validationError)
			return
		}

		createSalesRecord(data)
	}

	return (
		<ResponsiveModal open={!!dialogState} onOpenChange={nextOpen => !nextOpen && onClose()}>
			<DraggableResponsiveModalContent
				crossIconClassName="top-3 lg:top-4.5 cursor-pointer"
				className="gap-0 !max-w-[1240px] !overflow-visible !px-4 lg:!px-6 !py-4 max-h-[90vh] overflow-y-auto">
				<ResponsiveModalHeader className="draggable-dialog-handle max-lg:pb-2 lg:cursor-move lg:select-none">
					<ResponsiveModalTitle className="mb-0 max-lg:text-left">
						Verkaufsdatensatz erstellen
					</ResponsiveModalTitle>
					<ResponsiveModalDescription className="text-left text-secondary/50 font-medium">
						{dialogState
							? `Neuer Verkaufsdatensatz fuer ${dialogState.userName || `User ${dialogState.userId}`}`
							: 'Neuen Verkaufsdatensatz erstellen'}
					</ResponsiveModalDescription>
				</ResponsiveModalHeader>

				<Form {...salesRecordForm}>
					<form
						onSubmit={salesRecordForm.handleSubmit(onSubmit)}
						className="mt-4 flex flex-col gap-4">
						<div className="rounded-[14px] border border-[#ECECEC] bg-[#F7F8F880] px-4 py-3">
							<p className="text-sm font-extrabold text-secondary">
								User ID: {dialogState?.userId ?? '-'}
							</p>
						</div>
						<p className="text-sm text-secondary/60 font-medium">
							Dieses Formular erstellt immer genau einen neuen Verkaufsdatensatz.
						</p>
						<SalesRecordCreateFields
							control={salesRecordForm.control}
							handleDecimalFieldChange={handleDecimalFieldChange}
							handleDecimalFieldBlur={handleDecimalFieldBlur}
							bookingOptions={bookings ? bookings.data.map(booking => booking.id.toString()) : []}
							isBookingOptionsLoading={bookingsStatus === 'loading'}
						/>
						<Button
							type="submit"
							variant="secondary"
							disabled={isSavingSalesRecord}
							className="mt-2 w-full lg:h-[44px] rounded-[12px] font-extrabold text-white">
							{isSavingSalesRecord && <Loader2 className="animate-spin" />}
							{isSavingSalesRecord ? 'Bitte warten' : 'Speichern'}
						</Button>
					</form>
				</Form>
			</DraggableResponsiveModalContent>
		</ResponsiveModal>
	)
}

const SalesRecordHistoryRow = ({
	record,
	isEditing,
	editDraft,
	onStartEdit,
	onCancelEdit,
	onDraftChange,
	onDraftBlur,
	onSaveEdit,
	onDelete,
	bookingOptions,
	isBookingOptionsLoading,
	isSubmitting,
}: {
	record: SalesRecord
	isEditing: boolean
	editDraft: SalesRecordFormRow | null
	onStartEdit: (record: SalesRecord) => void
	onCancelEdit: () => void
	onDraftChange: (key: keyof SalesRecordFormRow, value: string) => void
	onDraftBlur: (key: keyof SalesRecordFormRow, value: string) => void
	onSaveEdit: () => void
	onDelete: (record: SalesRecord) => void
	bookingOptions: string[]
	isBookingOptionsLoading: boolean
	isSubmitting: boolean
}) => {
	const values = isEditing && editDraft ? editDraft : mapSalesRecordToFormRow(record)

	return (
		<>
			<div className="hidden lg:grid grid-cols-[120px_170px_130px_130px_150px_150px_1fr_104px] border-b border-[#ECECEC] last:border-b-0">
				{salesRecordColumns.map(column => (
					<div key={`${record.id}-${column.key}`} className="p-2">
						{isEditing ? (
							column.key === 'booking_id' ? (
								<VirtualizedCombobox
									searchPlaceholder="Buchung waehlen"
									onChange={value => onDraftChange(column.key, value)}
									value={values[column.key] ?? ''}
									isLoading={isBookingOptionsLoading}
									height={isBookingOptionsLoading ? '40px' : '250px'}
									width="100%"
									contentClassName="lg:w-[200px]!"
									options={bookingOptions}
								/>
							) : column.type === 'textarea' ? (
								<Textarea
									value={values[column.key] ?? ''}
									onChange={e => onDraftChange(column.key, e.target.value)}
									className="min-h-11 h-full rounded-[10px] resize-none"
									placeholder={column.placeholder}
								/>
							) : (
								<Input
									value={values[column.key] ?? ''}
									onChange={e => onDraftChange(column.key, e.target.value)}
									onBlur={e => onDraftBlur(column.key, e.target.value)}
									className="h-11 rounded-[10px]"
									inputMode={column.inputMode}
									placeholder={column.placeholder}
								/>
							)
						) : (
							<div className="min-h-11 rounded-[10px] px-3 py-3 text-sm text-secondary bg-[#FAFAFA] whitespace-pre-wrap break-words">
								{formatSalesRecordFieldForView(column.key, values[column.key])}
							</div>
						)}
					</div>
				))}
				<div className="p-2 flex items-start justify-center gap-1">
					{isEditing ? (
						<>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-11 px-2 text-emerald-600"
								onClick={onSaveEdit}
								disabled={isSubmitting}>
								<Check className="size-4" />
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-11 px-2 text-secondary/60"
								onClick={onCancelEdit}
								disabled={isSubmitting}>
								<X className="size-4" />
							</Button>
						</>
					) : (
						<>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-11 px-2"
								onClick={() => onStartEdit(record)}>
								<Pencil className="size-4" />
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-11 px-2 text-destructive"
								onClick={() => onDelete(record)}>
								<Trash2 className="size-4" />
							</Button>
						</>
					)}
				</div>
			</div>

			<div className="lg:hidden rounded-[16px] border border-[#ECECEC] p-4 flex flex-col gap-3">
				<div className="flex items-center justify-between gap-3">
					<p className="font-extrabold text-secondary">Datensatz #{record.id}</p>
					<div className="flex items-center gap-1">
						{isEditing ? (
							<>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={onSaveEdit}
									disabled={isSubmitting}>
									<Check className="size-4" />
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={onCancelEdit}
									disabled={isSubmitting}>
									<X className="size-4" />
								</Button>
							</>
						) : (
							<>
								<Button type="button" variant="ghost" size="sm" onClick={() => onStartEdit(record)}>
									<Pencil className="size-4" />
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="text-destructive"
									onClick={() => onDelete(record)}>
									<Trash2 className="size-4" />
								</Button>
							</>
						)}
					</div>
				</div>

				{salesRecordColumns.map(column => (
					<div key={`${record.id}-${column.key}-mobile`} className="flex flex-col gap-1">
						<p className="text-[11px] font-bold uppercase tracking-[0.08em] text-secondary/60">
							{column.label}
						</p>
						{isEditing ? (
							column.key === 'booking_id' ? (
								<VirtualizedCombobox
									searchPlaceholder="Buchung waehlen"
									onChange={value => onDraftChange(column.key, value)}
									value={values[column.key] ?? ''}
									isLoading={isBookingOptionsLoading}
									height={isBookingOptionsLoading ? '40px' : '250px'}
									width="100%"
									contentClassName="lg:w-[200px]!"
									options={bookingOptions}
								/>
							) : column.type === 'textarea' ? (
								<Textarea
									value={values[column.key] ?? ''}
									onChange={e => onDraftChange(column.key, e.target.value)}
									className="min-h-10 rounded-[12px] resize-none"
									placeholder={column.placeholder}
								/>
							) : (
								<Input
									value={values[column.key] ?? ''}
									onChange={e => onDraftChange(column.key, e.target.value)}
									onBlur={e => onDraftBlur(column.key, e.target.value)}
									className="rounded-[12px]"
									inputMode={column.inputMode}
									placeholder={column.placeholder}
								/>
							)
						) : (
							<div className="rounded-[12px] bg-[#FAFAFA] px-3 py-2 text-sm text-secondary whitespace-pre-wrap break-words">
								{formatSalesRecordFieldForView(column.key, values[column.key])}
							</div>
						)}
					</div>
				))}
			</div>
		</>
	)
}

export const SalesRecordsHistoryDialog = ({
	dialogState,
	onClose,
	onSuccess,
}: SharedSalesRecordDialogProps) => {
	const queryClient = useQueryClient()
	const [editingRecordId, setEditingRecordId] = useState<number | null>(null)
	const [editDraft, setEditDraft] = useState<SalesRecordFormRow | null>(null)
	const [recordPendingDelete, setRecordPendingDelete] = useState<SalesRecord | null>(null)

	const { data: salesRecords = [], isLoading: isLoadingSalesRecords } = useQuery(
		getSalesRecordQueryKey(dialogState?.userId),
		() => bookingService.fetchSalesRecordsByUser(dialogState!.userId),
		{
			enabled: !!dialogState?.userId,
			refetchOnWindowFocus: false,
			onError: (err: any) => {
				toast.error(
					err?.response?.data?.response?.message ??
						'Verkaufsdaten konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
				)
			},
		},
	)

	useEffect(() => {
		if (!dialogState) {
			setEditingRecordId(null)
			setEditDraft(null)
			setRecordPendingDelete(null)
		}
	}, [dialogState])

	const recordsById = useMemo(
		() => new Map(salesRecords.map(record => [record.id, record])),
		[salesRecords],
	)

	const handleDraftChange = (key: keyof SalesRecordFormRow, value: string) => {
		setEditDraft(current => {
			if (!current) return current

			if (key === 'booking_id' && !/^\d*$/.test(value)) {
				return current
			}

			if (decimalFieldKeys.includes(key)) {
				const normalized = value.replace('.', ',')

				if (!/^\d*(?:[.,]\d{0,2})?$/.test(normalized)) {
					return current
				}

				return {
					...current,
					[key]: normalized,
				}
			}

			return {
				...current,
				[key]: value,
			}
		})
	}

	const handleDraftBlur = (key: keyof SalesRecordFormRow, value: string) => {
		if (!decimalFieldKeys.includes(key)) return

		const formatted = formatDecimalValue(value)

		setEditDraft(current => {
			if (!current) return current
			return {
				...current,
				[key]: formatted,
			}
		})
	}

	const { data: bookings, status: bookingsStatus } = useQuery(
		['fetch-all-bookings', dialogState?.userId],
		() =>
			bookingService.fetchBookingsData({
				pageNumber: '1',
				pageSize: '999999999999999',
			}),
		{
			enabled: !!dialogState,
			refetchOnWindowFocus: false,
			cacheTime: 1000 * 60 * 2,
			onError: () => {
				toast.error('Error fetching bookings')
			},
		},
	)

	const { mutate: saveEditedSalesRecords, isLoading: isSavingEditedSalesRecord } = useMutation({
		mutationKey: 'update-sales-records',
		mutationFn: async (draft: SalesRecordFormRow) => {
			if (!dialogState) return null

			const updatedRecords = salesRecords.map(record =>
				record.id === draft.id
					? toSalesRecordPayload(draft, dialogState.userId)
					: toSalesRecordPayload(mapSalesRecordToFormRow(record), dialogState.userId),
			)

			return bookingService.replaceSalesRecords({
				user_id: dialogState.userId,
				records: updatedRecords,
			})
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries(getSalesRecordQueryKey(dialogState?.userId))
			toast.success('Verkaufsdatensatz erfolgreich aktualisiert')
			setEditingRecordId(null)
			setEditDraft(null)
			onSuccess?.()
		},
		onError: (err: any) => {
			toast.error(
				err?.response?.data?.response?.message ??
					'Verkaufsdatensatz konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
			)
		},
	})

	const { mutate: deleteSalesRecord, isLoading: isDeletingSalesRecord } = useMutation({
		mutationKey: 'delete-sales-record',
		mutationFn: async (recordId: number) => bookingService.deleteSalesRecord(recordId),
		onSuccess: async () => {
			await queryClient.invalidateQueries(getSalesRecordQueryKey(dialogState?.userId))
			toast.success('Verkaufsdatensatz erfolgreich geloescht')
			setRecordPendingDelete(null)
			if (editingRecordId && !recordsById.has(editingRecordId)) {
				setEditingRecordId(null)
				setEditDraft(null)
			}
			onSuccess?.()
		},
		onError: (err: any) => {
			toast.error(
				err?.response?.data?.response?.message ??
					'Verkaufsdatensatz konnte nicht geloescht werden. Bitte versuchen Sie es erneut.',
			)
		},
	})

	const handleStartEdit = (record: SalesRecord) => {
		setEditingRecordId(record.id)
		setEditDraft(mapSalesRecordToFormRow(record))
	}

	const handleCancelEdit = () => {
		setEditingRecordId(null)
		setEditDraft(null)
	}

	const handleSaveEdit = () => {
		if (!editDraft) return

		const validationError = validateSalesRecordRow(editDraft)
		if (validationError) {
			toast.error(validationError)
			return
		}

		saveEditedSalesRecords(editDraft)
	}

	return (
		<>
			<ResponsiveModal open={!!dialogState} onOpenChange={nextOpen => !nextOpen && onClose()}>
				<DraggableResponsiveModalContent
					crossIconClassName="top-3 lg:top-4.5 cursor-pointer"
					className="gap-0 !overflow-visible max-lg:!max-w-[1240px] !max-w-[1340px] !px-4 lg:!px-6 !py-4 max-h-[90vh] !overflow-y-auto">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left flex items-center gap-2">
							<Eye className="size-5" />
							Verkaufsdatensaetze
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="text-left text-secondary/50 font-medium">
							{dialogState
								? `Alle Verkaufsdatensaetze fuer ${dialogState.userName || `User ${dialogState.userId}`}`
								: 'Verkaufsdatensaetze anzeigen'}
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>

					{isLoadingSalesRecords ? (
						<div className="py-12 flex items-center justify-center text-secondary">
							<Loader2 className="size-5 animate-spin" />
						</div>
					) : salesRecords.length === 0 ? (
						<div className="mt-4 flex flex-col gap-4">
							<div className="rounded-[14px] border border-[#ECECEC] bg-[#F7F8F880] px-4 py-3">
								<p className="text-sm font-extrabold text-secondary">
									User ID: {dialogState?.userId ?? '-'}
								</p>
							</div>
							<div className="rounded-[16px] border border-dashed border-input px-6 py-10 text-center text-sm text-secondary/60">
								Noch keine Verkaufsdatensaetze vorhanden.
							</div>
						</div>
					) : (
						<div className="mt-4 flex flex-col gap-4">
							<div className="rounded-[14px] border border-[#ECECEC] bg-[#F7F8F880] px-4 py-3">
								<p className="text-sm font-extrabold text-secondary">
									User ID: {dialogState?.userId ?? '-'}
								</p>
							</div>
							<div className="hidden lg:block overflow-x-auto rounded-[16px] border border-[#ECECEC]">
								<div className="min-w-[1240px]">
									<div className="grid grid-cols-[120px_170px_130px_130px_150px_150px_1fr_104px] border-b border-[#ECECEC] bg-[#F7F8F880]">
										{salesRecordColumns.map(column => (
											<div
												key={column.key}
												className="px-3 py-3 text-[11px] leading-4 font-bold uppercase tracking-[0.08em] text-secondary/60 whitespace-normal break-words">
												{column.label}
											</div>
										))}
										<div className="px-3 py-3 text-[11px] leading-4 font-bold uppercase tracking-[0.08em] text-secondary/60 text-center whitespace-normal break-words">
											Aktion
										</div>
									</div>
									{salesRecords.map(record => (
										<SalesRecordHistoryRow
											key={record.id}
											record={record}
											isEditing={editingRecordId === record.id}
											editDraft={editDraft}
											onStartEdit={handleStartEdit}
											onCancelEdit={handleCancelEdit}
											onDraftChange={handleDraftChange}
											onDraftBlur={handleDraftBlur}
											onSaveEdit={handleSaveEdit}
											bookingOptions={
												bookings ? bookings.data.map(booking => booking.id.toString()) : []
											}
											isBookingOptionsLoading={bookingsStatus === 'loading'}
											onDelete={setRecordPendingDelete}
											isSubmitting={isSavingEditedSalesRecord || isDeletingSalesRecord}
										/>
									))}
								</div>
							</div>

							<div className="flex flex-col gap-3 lg:hidden">
								{salesRecords.map(record => (
									<SalesRecordHistoryRow
										key={`${record.id}-mobile`}
										record={record}
										isEditing={editingRecordId === record.id}
										editDraft={editDraft}
										onStartEdit={handleStartEdit}
										bookingOptions={
											bookings ? bookings.data.map(booking => booking.id.toString()) : []
										}
										isBookingOptionsLoading={bookingsStatus === 'loading'}
										onCancelEdit={handleCancelEdit}
										onDraftChange={handleDraftChange}
										onDraftBlur={handleDraftBlur}
										onSaveEdit={handleSaveEdit}
										onDelete={setRecordPendingDelete}
										isSubmitting={isSavingEditedSalesRecord || isDeletingSalesRecord}
									/>
								))}
							</div>
						</div>
					)}
				</DraggableResponsiveModalContent>
			</ResponsiveModal>

			<ResponsiveModal
				open={!!recordPendingDelete}
				onOpenChange={nextOpen => !nextOpen && setRecordPendingDelete(null)}>
				<ResponsiveModalContent className="gap-0 !max-w-[460px]">
					<ResponsiveModalHeader className="max-lg:pb-2">
						<ResponsiveModalTitle className="mb-0 max-lg:text-left">
							Verkaufsdatensatz loeschen?
						</ResponsiveModalTitle>
						<ResponsiveModalDescription className="mt-4 text-left text-secondary/50 font-medium">
							Sind Sie sicher, dass Sie diesen Verkaufsdatensatz dauerhaft loeschen moechten?
						</ResponsiveModalDescription>
					</ResponsiveModalHeader>
					<div className="mt-6 flex gap-3">
						<Button
							type="button"
							variant="outline"
							className="flex-1 rounded-[12px]"
							onClick={() => setRecordPendingDelete(null)}
							disabled={isDeletingSalesRecord}>
							Abbrechen
						</Button>
						<Button
							type="button"
							variant="secondary"
							className="flex-1 rounded-[12px] bg-destructive text-white hover:bg-destructive/90"
							onClick={() => recordPendingDelete && deleteSalesRecord(recordPendingDelete.id)}
							disabled={isDeletingSalesRecord}>
							{isDeletingSalesRecord && <Loader2 className="animate-spin" />}
							{isDeletingSalesRecord ? 'Bitte warten' : 'Loeschen'}
						</Button>
					</div>
				</ResponsiveModalContent>
			</ResponsiveModal>
		</>
	)
}
